import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { verifyPin } from "../../Services/authService";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import Cookies from "js-cookie";
import Loader from "../../Loader";
import {
  BookCheckIcon,
  ShieldCheck,
  Ticket,
  TicketCheck,
  TicketMinus,
  XOctagon,
} from "lucide-react";

const PinInput = ({ inputRefs, onPinChange, onSubmit }) => {
  const [pin, setPin] = useState(["", "", "", "", "", ""]);
  const [focusedIndex, setFocusedIndex] = useState(0);

  const handleChange = (index, value) => {
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);

    const fullPin = newPin.join("");
    if (onPinChange) onPinChange(fullPin);

    // Move to next input
    if (value !== "" && index < 5) {
      inputRefs.current[index + 1].current.focus();
      setFocusedIndex(index + 1);
    }

    // Auto-submit when 6 digits are entered
    if (fullPin.length === 6 && onSubmit) {
      onSubmit(fullPin);
    }
  };

  const handleBackspace = (index, event) => {
    if (event.key === "Backspace" && index > 0 && pin[index] === "") {
      inputRefs.current[index - 1].current.focus();
      setFocusedIndex(index - 1);
    }
  };

  return (
    <div className="flex justify-center md:mb-4 mb-2 items-center relative">
      {/* Jump Animation CSS */}
      <style>
        {`
          .jump {
            display: inline-block;
            animation: jump 0.35s ease;
          }
          @keyframes jump {
            0% { transform: translateY(0); }
            50% { transform: translateY(-6px); }
            100% { transform: translateY(0); }
          }
        `}
      </style>

      {pin.map((value, index) => (
        <React.Fragment key={index}>
          <div className="relative">
            <input
              ref={inputRefs.current[index]}
              type="password"
              maxLength={1}
              value={value}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleBackspace(index, e)}
              onFocus={() => setFocusedIndex(index)}
              className="border border-blue-500 md:w-10 md:h-10 w-9 h-10 text-center mx-1 rounded shadow-lg shadow-blue-600"
              autoFocus={index === 0}
            />

            {/* {focusedIndex === index && (
              <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-xl jump text-green-800">
                ✔
              </span>
            )} */}
          </div>

          {index === 2 && (
            <span className="md:mt-2 text-sky-500 ml-1">&ndash;</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const ModalWithPinInput = ({
  isModalOpen,
  toggleModal,
  tempToken,
  setForgotPasswordClicked,
}) => {
  const inputRefs = useRef(
    Array.from({ length: 6 }).map(() => React.createRef()),
  );
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (currentPin = pin) => {
    if (currentPin.length !== 6) {
      setError("Please enter a 6-digit PIN.");
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      const result = await verifyPin(currentPin, tempToken);

      if (result.success) {
        // showSuccessToast(result.message);
        Cookies.set("authTokenUser", result.token);
        toggleModal();
        navigate("/Dashboard");
      } else {
        showErrorToast(result.message);
      }
    } catch (error) {
      showErrorToast("Server error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    isModalOpen && (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        {/* Loader Overlays Above Modal */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center z-50">
            <Loader />
          </div>
        )}

        <div className="bg-white rounded-3xl border-b-[15px] border-blue-700  shadow-2xl w-[78%] md:w-1/3 z-40">
          <div className="bg-gradient-to-r from-blue-500  to-blue-800 text-white text-center md:py-4 p-2 rounded-t-lg">
            <span className="text-md md:text-xl flex items-center justify-center gap-3 font-mono">
              {" "}
              <BookCheckIcon /> Enter 6 Digit PIN
            </span>
          </div>

          <div className="flex flex-col items-center justify-center text-center px-4 py-6">
            {/* Icon Container with soft ring and glow effect */}
            {/* <div className="relative flex items-center justify-center w-24 h-24 mb-6 bg-blue-50 rounded-full ring-4 ring-blue-50/50 shadow-[0_0_10px_-10px_rgba(16,185,129,0.6)]">
              <div className="relative">
                <ShieldCheck
                  strokeWidth={1.5}
                  size={70}
                  fill="#066FE0"
                  className="text-white"
                />

              </div>
            </div> */}
            {/* Custom Premium Shield SVG */}
            <div className="relative flex items-center justify-center mb-6">
              {/* Subtle background glow */}
              <div className="absolute inset-0 bg-blue-400 blur-[20px] opacity-20 rounded-full"></div>

              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-16 h-16 relative z-10 drop-shadow-md"
              >
                <path
                  d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z"
                  fill="url(#shield_gradient)"
                />
                {/* Thicker, rounded checkmark */}
                <path
                  d="M8.5 12L10.5 14L15.5 9"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <defs>
                  <linearGradient
                    id="shield_gradient"
                    x1="12"
                    y1="2"
                    x2="12"
                    y2="22"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#3A7FF3" /> {/* blue 400 */}
                    <stop offset="1" stopColor="#1E41B0" /> {/* blue 600 */}
                  </linearGradient>
                </defs>
              </svg>
            </div>

            {/* Typography */}
            <div className="space-y-2">
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-800 tracking-tight">
                Authenticate Your Account
              </h2>
              <p className="text-sm md:text-base text-slate-500 font-medium">
                Have Lost your Pin?{" "}
                <button
                  onClick={() => setForgotPasswordClicked()}
                  className="text-blue-600 text-lg"
                >
                  Reset here
                </button>
              </p>
            </div>
          </div>

          <div className="">
            <PinInput
              inputRefs={inputRefs}
              onPinChange={setPin}
              onSubmit={handleSubmit}
            />
            {error && (
              <div className="text-red-500 mt-2 text-center">{error}</div>
            )}
          </div>

          <span className="font-semibold text-blue-900 text-center block mt-2 text-lg">
            PIN will verify automatically
          </span>

          <div className="flex justify-center md:p-6 p-2">
            <button
              type="button"
              className="shadow-xl  bg-gradient-to-r from-rose-600
              to-rose-800 text-white md:text-xl py-1 flex items-center gap-2  px-6 rounded-md hover:from-red-900 hover:to-red-400"
              onClick={toggleModal}
              disabled={isLoading}
            >
              <XOctagon size={20} />
              Close
            </button>
          </div>
        </div>
      </div>
    )
  );
};

export default ModalWithPinInput;
